import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Paper,
  IconButton,
  Divider,
  Chip,
  Dialog,
  DialogContent,
} from '@mui/material';
import {
  School,
  ArrowForward,
  MenuBook,
  People,
  ChevronLeft,
  ChevronRight,
  Star,
  CheckCircle,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Grade as GradeIcon,
  Info as InfoIcon,
  AttachMoney,
  Gavel,
  Security,
  EmojiEvents,
  Download,
  DirectionsBus,
  Computer,
  Assignment,
  ZoomIn,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const schoolColors = {
  primary: '#1a237e',
  secondary: '#0d47a1',
  accent: '#ff6f00',
  gold: '#ffd700',
  dark: '#0a0a2a',
  light: '#e8f0fe',
  white: '#ffffff',
  green: '#2e7d32',
  teal: '#00695c',
  purple: '#6a1b9a',
};

const schoolImages = [
  {
    url: '/images/kaozya-secondary-admission.jpg',
    title: 'Nafasi za Kujiunga & Kuhamia Kidato cha 1 - 6',
    subtitle: 'Kaozya High School — Academic Year 2026',
    description: 'Combinations: PCM, PCB, PGM, EGM, HGE, HGK, HKL, HGL, HGFa & ICT. Ada nafuu zinalipwa kwa awamu 4.',
    buttonText: 'Secondary Application',
    secondaryButtonText: 'Download Fomu (PDF)',
    link: '/apply',
    downloadLink: '/downloads/FOMU_YA_KUJIUNGA_NA_KAOZYA_HIGH_SCHOOL.pdf',
    badge: 'Form 1 - 6 Open',
  },
  {
    url: '/images/kaozya-primary-admission.jpg',
    title: 'Kaozya English Medium Pre & Primary School',
    subtitle: 'Elimu Bora na Malezi kwa Wote | Reg: EM. 21666',
    description: 'Nafasi za masomo kuanzia Pre-School hadi Darasa la Sita. Ada inajumuisha kifungua kinywa, chakula cha mchana na usafiri wa shule.',
    buttonText: 'Primary Application',
    secondaryButtonText: 'Download Fomu (PDF)',
    link: '/primary/apply',
    downloadLink: '/downloads/FOMU_YA_KUJIUNGA_NA_KAOZYA_ENGLISH_MEDIUM.pdf',
    badge: 'Pre & Primary Open',
  },
  {
    url: '/images/kaozya-pre-form-one.jpg',
    title: 'Nafasi za Masomo ya Pre-Form One 2026',
    subtitle: 'Kuanzia 12/09/2026 Hadi 20/12/2026 (Miezi 3)',
    description: 'Masomo: English, Physics, Chemistry, Mathematics, Biology, Geography, Business, Computer Course & Maadili. Jiandikishe Mapema!',
    buttonText: 'Jiunge na Pre-Form One',
    secondaryButtonText: 'Angalia Ada & Ratiba',
    link: '/apply',
    downloadLink: '/fees',
    badge: 'Pre-Form 1 Intake',
  },
  {
    url: '/images/kaozya-high-school-campus.jpg',
    title: 'Mazingira Safi, Tulivu na Vifaa vya Kisasa',
    subtitle: 'Quality Education For A Better Future — Mlimba, Morogoro',
    description: 'Shule ya Bweni na Kutwa kwa wavulana na wasichana. Pia inatoa: Pre-Form 5, QT, Resitters, Private Candidates (PC) & Computer Course.',
    buttonText: 'Kuhusu Shule',
    secondaryButtonText: 'Sheria za Shule',
    link: '/about',
    downloadLink: '/rules',
    badge: 'Campus & Facilities',
  },
  {
    url: '/images/kaozya-primary-campus.jpg',
    title: 'Usafiri wa School Bus & Malezi ya Upendo',
    subtitle: 'Baby Class, Kindergarten, Upper Class, Pre-Unit & Std 1–6',
    description: 'Mchepuo wa Kiingereza (English Medium) wenye walimu waliobobea, chakula bora, usalama wa saa 24 na malezi yenye nidhamu.',
    buttonText: 'Omba Nafasi ya Msingi',
    secondaryButtonText: 'Mawasiliano',
    link: '/primary/apply',
    downloadLink: '/about',
    badge: 'English Medium',
  },
];

const galleryItems = [
  {
    title: 'Kaozya High School - Form 1-6 & Pre-Form One',
    desc: 'Official admission poster with complete combination options & 4-term installment plan.',
    image: '/images/kaozya-secondary-admission.jpg',
    category: 'Secondary & High School',
  },
  {
    title: 'Kaozya English Medium Primary School',
    desc: 'Pre-School and Primary admissions with breakfast, lunch & transportation included.',
    image: '/images/kaozya-primary-admission.jpg',
    category: 'English Medium Primary',
  },
  {
    title: 'Pre-Form One Intensive Preparation',
    desc: 'Preparation program covering core sciences, mathematics, English and computer course.',
    image: '/images/kaozya-pre-form-one.jpg',
    category: 'Pre-Form One',
  },
  {
    title: 'Campus Corridor, Signpost & Main Gate',
    desc: 'Kaozya Road, Kalengakelu, Mlimba Morogoro — Safe, modern learning compound.',
    image: '/images/kaozya-high-school-campus.jpg',
    category: 'School Compound',
  },
  {
    title: 'School Transport Bus & Primary Classrooms',
    desc: 'Comfortable student transportation bus service and bright learning classrooms.',
    image: '/images/kaozya-primary-campus.jpg',
    category: 'Transport & Classes',
  },
];

const HomePage = () => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedPreviewImage, setSelectedPreviewImage] = useState(null);
  const [admissionWindow, setAdmissionWindow] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % schoolImages.length);
    }, 7000);

    // Fetch admission window settings
    fetch('https://kaozyaschools.ac.tz/backend_api.php/admission-settings')
      .then(res => res.json())
      .then(data => { if (data && data.window_open_date) setAdmissionWindow(data); })
      .catch(() => {});

    return () => clearInterval(timer);
  }, []);

  const handlePrevImage = () => {
    setCurrentImageIndex(prev => (prev - 1 + schoolImages.length) % schoolImages.length);
  };

  const handleNextImage = () => {
    setCurrentImageIndex(prev => (prev + 1) % schoolImages.length);
  };

  return (
    <Box sx={{ overflowX: 'hidden', bgcolor: '#f4f7fb', minHeight: '100vh' }}>

      {/* ── Dynamic Admission Window Banner ── */}
      {admissionWindow && (
        <Box sx={{ bgcolor: admissionWindow.is_open ? schoolColors.accent : '#b91c1c', color: 'white', py: 1.2, px: 2, textAlign: 'center' }}>
          <Container maxWidth="xl">
            <Typography variant="body2" sx={{ fontWeight: 800, fontSize: { xs: '0.8rem', md: '0.95rem' } }}>
              📢 DIRISHA LA MAOMBI {admissionWindow.academic_year || '2026/2027'}: {admissionWindow.is_open ? 'LIKO WAZI' : 'LIMEFUNGWA'} (Tarehe ya Kuanza: {admissionWindow.window_open_date} | Tarehe ya Kufunga: {admissionWindow.window_close_date}) — {admissionWindow.announcement_sw || 'Nafasi za masomo zipo wazi.'}
            </Typography>
          </Container>
        </Box>
      )}

      {/* ── Hero Slider with Actual Posters ── */}
      <Box sx={{ position: 'relative', bgcolor: schoolColors.dark, color: 'white', overflow: 'hidden' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 4, md: 8 }, position: 'relative', zIndex: 2 }}>
          <Grid container spacing={4} alignItems="center">
            {/* Text side */}
            <Grid item xs={12} md={7}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentImageIndex}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 30 }}
                  transition={{ duration: 0.5 }}
                >
                  <Chip
                    label={schoolImages[currentImageIndex].badge}
                    sx={{
                      bgcolor: schoolColors.accent,
                      color: 'white',
                      fontWeight: 800,
                      fontSize: '0.85rem',
                      mb: 2,
                      px: 1,
                    }}
                  />
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 900,
                      fontSize: { xs: '1.85rem', sm: '2.5rem', md: '3.2rem' },
                      lineHeight: 1.15,
                      color: 'white',
                      mb: 1.5,
                      textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                    }}
                  >
                    {schoolImages[currentImageIndex].title}
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      color: schoolColors.gold,
                      fontWeight: 700,
                      mb: 2,
                      fontSize: { xs: '1rem', md: '1.25rem' },
                    }}
                  >
                    {schoolImages[currentImageIndex].subtitle}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      fontSize: { xs: '0.95rem', md: '1.1rem' },
                      lineHeight: 1.7,
                      mb: 4,
                      maxWidth: 620,
                    }}
                  >
                    {schoolImages[currentImageIndex].description}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => navigate(schoolImages[currentImageIndex].link)}
                      endIcon={<ArrowForward />}
                      sx={{
                        py: 1.4,
                        px: 3.5,
                        fontSize: '1rem',
                        fontWeight: 800,
                        bgcolor: schoolColors.accent,
                        borderRadius: 3,
                        boxShadow: '0 8px 24px rgba(255,111,0,0.4)',
                        '&:hover': { bgcolor: schoolColors.gold, color: schoolColors.dark },
                      }}
                    >
                      {schoolImages[currentImageIndex].buttonText}
                    </Button>

                    <Button
                      variant="outlined"
                      size="large"
                      href={schoolImages[currentImageIndex].downloadLink.startsWith('/downloads') ? schoolImages[currentImageIndex].downloadLink : undefined}
                      onClick={!schoolImages[currentImageIndex].downloadLink.startsWith('/downloads') ? () => navigate(schoolImages[currentImageIndex].downloadLink) : undefined}
                      download={schoolImages[currentImageIndex].downloadLink.startsWith('/downloads')}
                      startIcon={schoolImages[currentImageIndex].downloadLink.startsWith('/downloads') ? <Download /> : <InfoIcon />}
                      sx={{
                        py: 1.4,
                        px: 3,
                        fontSize: '1rem',
                        fontWeight: 700,
                        borderColor: schoolColors.gold,
                        color: schoolColors.gold,
                        borderRadius: 3,
                        '&:hover': { bgcolor: `${schoolColors.gold}20`, borderColor: schoolColors.gold },
                      }}
                    >
                      {schoolImages[currentImageIndex].secondaryButtonText}
                    </Button>
                  </Box>

                  {/* Highlights under Hero */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', pt: 2, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ color: schoolColors.gold, fontSize: 18 }} />
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                        Awamu Nne za Malipo
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ color: schoolColors.gold, fontSize: 18 }} />
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                        Bweni &amp; Kutwa
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ color: schoolColors.gold, fontSize: 18 }} />
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                        School Bus &amp; Chakula
                      </Typography>
                    </Box>
                  </Box>
                </motion.div>
              </AnimatePresence>
            </Grid>

            {/* Visual Image / Poster side */}
            <Grid item xs={12} md={5}>
              <Box sx={{ position: 'relative', textAlign: 'center' }}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentImageIndex}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Paper
                      elevation={12}
                      sx={{
                        borderRadius: 4,
                        overflow: 'hidden',
                        border: `3px solid ${schoolColors.gold}`,
                        boxShadow: '0 20px 40px rgba(0,0,0,0.6)',
                        cursor: 'pointer',
                        position: 'relative',
                        maxHeight: { xs: 380, sm: 480, md: 540 },
                        bgcolor: '#000',
                      }}
                      onClick={() => setSelectedPreviewImage(schoolImages[currentImageIndex].url)}
                    >
                      <Box
                        component="img"
                        src={schoolImages[currentImageIndex].url}
                        alt={schoolImages[currentImageIndex].title}
                        sx={{
                          width: '100%',
                          height: { xs: 380, sm: 480, md: 540 },
                          objectFit: 'contain',
                          bgcolor: '#111',
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
                          p: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
                          Official Kaozya Announcement
                        </Typography>
                        <Chip
                          icon={<ZoomIn sx={{ color: 'white !important' }} />}
                          label="Click to Zoom"
                          size="small"
                          sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', height: 24, fontSize: '0.7rem' }}
                        />
                      </Box>
                    </Paper>
                  </motion.div>
                </AnimatePresence>

                {/* Slider Controls */}
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2.5 }}>
                  <IconButton
                    onClick={handlePrevImage}
                    sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', '&:hover': { bgcolor: schoolColors.accent } }}
                  >
                    <ChevronLeft />
                  </IconButton>

                  {schoolImages.map((_, i) => (
                    <Box
                      key={i}
                      onClick={() => setCurrentImageIndex(i)}
                      sx={{
                        width: i === currentImageIndex ? 30 : 10,
                        height: 10,
                        borderRadius: 5,
                        bgcolor: i === currentImageIndex ? schoolColors.gold : 'rgba(255,255,255,0.3)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                      }}
                    />
                  ))}

                  <IconButton
                    onClick={handleNextImage}
                    sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', '&:hover': { bgcolor: schoolColors.accent } }}
                  >
                    <ChevronRight />
                  </IconButton>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Official Document Download Banner ── */}
      <Box sx={{ bgcolor: schoolColors.gold, py: 2.5, px: 2 }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Assignment sx={{ color: schoolColors.dark, fontSize: 32 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: schoolColors.dark, lineHeight: 1.2 }}>
                    Official Admission Packages &amp; Forms 2026
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#2b2b2b' }}>
                    Download official 14-page registration forms with full fee schedule &amp; school rules
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1.5, justifyContent: { xs: 'flex-start', md: 'flex-end' }, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  href="/downloads/FOMU_YA_KUJIUNGA_NA_KAOZYA_HIGH_SCHOOL.pdf"
                  download="FOMU_YA_KUJIUNGA_NA_KAOZYA_HIGH_SCHOOL.pdf"
                  startIcon={<Download />}
                  sx={{
                    bgcolor: schoolColors.primary,
                    color: 'white',
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: 2,
                    fontSize: '0.85rem',
                    '&:hover': { bgcolor: schoolColors.dark },
                  }}
                >
                  Secondary Form (PDF)
                </Button>
                <Button
                  variant="contained"
                  href="/downloads/FOMU_YA_KUJIUNGA_NA_KAOZYA_ENGLISH_MEDIUM.pdf"
                  download="FOMU_YA_KUJIUNGA_NA_KAOZYA_ENGLISH_MEDIUM.pdf"
                  startIcon={<Download />}
                  sx={{
                    bgcolor: schoolColors.green,
                    color: 'white',
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: 2,
                    fontSize: '0.85rem',
                    '&:hover': { bgcolor: '#1b5e20' },
                  }}
                >
                  Primary Form (PDF)
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Official Gallery / Visual Showcase ── */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Chip label="Official Photo & Announcement Gallery" color="primary" sx={{ mb: 1.5, fontWeight: 700 }} />
          <Typography variant="h3" sx={{ fontWeight: 800, color: schoolColors.primary, mb: 1 }}>
            Kaozya Schools in Pictures
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 650, mx: 'auto' }}>
            Explore genuine announcements, student life, campus facilities, and school transportation.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {galleryItems.map((item, idx) => (
            <Grid item xs={12} sm={6} md={idx < 2 ? 6 : 4} key={idx}>
              <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.3 }}>
                <Card
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    border: '1px solid rgba(0,0,0,0.06)',
                  }}
                  onClick={() => setSelectedPreviewImage(item.image)}
                >
                  <Box sx={{ position: 'relative', height: idx < 2 ? 300 : 220, bgcolor: '#111' }}>
                    <CardMedia
                      component="img"
                      image={item.image}
                      alt={item.title}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <Chip
                      label={item.category}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 12,
                        left: 12,
                        bgcolor: `${schoolColors.primary}E6`,
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.72rem',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 12,
                        right: 12,
                        bgcolor: 'rgba(0,0,0,0.7)',
                        borderRadius: '50%',
                        p: 0.5,
                        display: 'flex',
                      }}
                    >
                      <ZoomIn sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                  </Box>
                  <CardContent sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: schoolColors.primary, mb: 0.5, fontSize: '1.05rem' }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, flexGrow: 1 }}>
                      {item.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── Secondary & High School Admission Cards ── */}
      <Box sx={{ bgcolor: schoolColors.light, py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip label="Reg. No: S.4913 | NECTA: S.5430" sx={{ mb: 1.5, bgcolor: `${schoolColors.primary}20`, color: schoolColors.primary, fontWeight: 700 }} />
            <Typography variant="h3" sx={{ fontWeight: 800, color: schoolColors.primary, mb: 1 }}>
              Secondary &amp; High School Admissions
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 650, mx: 'auto' }}>
              Nafasi za kujiunga na kuhamia kuanzia Kidato cha Kwanza hadi Sita kwa mwaka 2026.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {/* Card 1: Form 1 - 4 */}
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 4, height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }}>
                <Box sx={{ p: 3, bgcolor: schoolColors.primary, color: 'white' }}>
                  <Chip label="O-Level Education" size="small" sx={{ bgcolor: schoolColors.gold, color: schoolColors.dark, fontWeight: 800, mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 900 }}>KIDATO CHA 1 - 4</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>Kutwa na Bweni (Wavulana &amp; Wasichana)</Typography>
                </Box>
                <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle2" sx={{ color: schoolColors.primary, fontWeight: 800, mb: 1.5 }}>
                    ADA YA MASOMO (KWA MWAKA):
                  </Typography>
                  <Box sx={{ bgcolor: '#f1f5f9', p: 2, borderRadius: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Kutwa (Day):</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: schoolColors.accent }}>TSh 1,000,000/=</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Bweni (Boarding):</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: schoolColors.primary }}>TSh 1,400,000/=</Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
                    ✓ Ada inalipwa kwa awamu nne (Jan, Apr, Jul, Sept)
                  </Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, flexGrow: 1 }}>
                    Masomo: Physics, Chemistry, Biology, Mathematics, Geography, History, English, Civics, Kiswahili, Literature, Book keeping, Business studies, Computer &amp; Maadili.
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => navigate('/apply')}
                    sx={{ bgcolor: schoolColors.accent, fontWeight: 700, borderRadius: 2, py: 1.2, '&:hover': { bgcolor: schoolColors.gold, color: schoolColors.dark } }}
                    endIcon={<ArrowForward />}
                  >
                    Apply Form 1-4
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Card 2: Form 5 - 6 */}
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 4, height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', border: `2px solid ${schoolColors.secondary}` }}>
                <Box sx={{ p: 3, bgcolor: schoolColors.secondary, color: 'white' }}>
                  <Chip label="A-Level High School" size="small" sx={{ bgcolor: schoolColors.gold, color: schoolColors.dark, fontWeight: 800, mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 900 }}>KIDATO CHA 5 &amp; 6</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>Science, Business &amp; Arts Combinations</Typography>
                </Box>
                <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle2" sx={{ color: schoolColors.primary, fontWeight: 800, mb: 1.5 }}>
                    ADA YA MASOMO (KWA MWAKA):
                  </Typography>
                  <Box sx={{ bgcolor: '#f1f5f9', p: 2, borderRadius: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Kutwa (Day):</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: schoolColors.accent }}>TSh 1,200,000/=</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Bweni (Boarding):</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: schoolColors.primary }}>TSh 1,600,000/=</Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                    Combinations zilizopo:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 2 }}>
                    {['PCM', 'PCB', 'PGM', 'EGM', 'HGE', 'HGK', 'HKL', 'HGL', 'HGFa', 'ICT'].map(c => (
                      <Chip key={c} label={c} size="small" sx={{ bgcolor: `${schoolColors.secondary}15`, color: schoolColors.secondary, fontWeight: 700, fontSize: '0.72rem' }} />
                    ))}
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => navigate('/apply')}
                    sx={{ bgcolor: schoolColors.secondary, fontWeight: 700, borderRadius: 2, py: 1.2, mt: 'auto', '&:hover': { bgcolor: schoolColors.primary } }}
                    endIcon={<ArrowForward />}
                  >
                    Apply Form 5-6
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Card 3: Pre-Form One & Special Programs */}
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 4, height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }}>
                <Box sx={{ p: 3, bgcolor: '#00695c', color: 'white' }}>
                  <Chip label="Special Intake 2026" size="small" sx={{ bgcolor: schoolColors.gold, color: schoolColors.dark, fontWeight: 800, mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 900 }}>PRE-FORM ONE</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>12/09/2026 Hadi 20/12/2026 (Miezi 3)</Typography>
                </Box>
                <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle2" sx={{ color: '#00695c', fontWeight: 800, mb: 1.5 }}>
                    ADA YA MIEZI MITATU:
                  </Typography>
                  <Box sx={{ bgcolor: '#f1f5f9', p: 2, borderRadius: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Kutwa (Day):</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: schoolColors.accent }}>TSh 150,000/=</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Bweni (Boarding):</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: schoolColors.primary }}>TSh 200,000/=</Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                    Pia Shule Inatoa:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 2 }}>
                    {['Pre-Form 5', 'QT', 'Resitters', 'Private Candidates (PC)', 'Computer Course'].map(item => (
                      <Chip key={item} label={item} size="small" sx={{ bgcolor: '#00695c15', color: '#00695c', fontWeight: 600, fontSize: '0.72rem' }} />
                    ))}
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => navigate('/apply')}
                    sx={{ bgcolor: '#00695c', fontWeight: 700, borderRadius: 2, py: 1.2, mt: 'auto', '&:hover': { bgcolor: '#004d40' } }}
                    endIcon={<ArrowForward />}
                  >
                    Apply Pre-Form 1
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Primary School (English Medium) Section ── */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                overflow: 'hidden',
                border: `3px solid ${schoolColors.green}`,
                bgcolor: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(46, 125, 50, 0.25)',
              }}
            >
              <Box
                component="img"
                src="/images/kaozya-english-medium-header.jpg"
                alt="Kaozya English Medium Logo"
                sx={{
                  width: '380%',
                  height: 'auto',
                  objectFit: 'cover',
                  objectPosition: 'left center',
                  transform: 'scale(1.2) translateX(36%)',
                }}
              />
            </Box>
          </Box>
          <Chip label="Reg: EM. 21666 | Exam Centre: PS11011193" sx={{ mb: 1.5, bgcolor: `${schoolColors.green}20`, color: schoolColors.green, fontWeight: 700 }} />
          <Typography variant="h3" sx={{ fontWeight: 800, color: schoolColors.green, mb: 1 }}>
            KAOZYA ENGLISH MEDIUM SCHOOL
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 650, mx: 'auto' }}>
            Nafasi za masomo kuanzia Baby Class, Kindergarten, Upper Class, Pre-Unit hadi Darasa la Sita. P.O. BOX 188, KALENGAKELU, MLIMBA-MOROGORO.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Pre-School Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 4, height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', border: `2px solid ${schoolColors.green}40` }}>
              <Box sx={{ p: 3, bgcolor: schoolColors.green, color: 'white' }}>
                <Chip label="Ages 3 - 5 Years" size="small" sx={{ bgcolor: schoolColors.gold, color: schoolColors.dark, fontWeight: 800, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 900 }}>PRE-SCHOOL / CHEKECHEA</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Baby Class · Kindergarten · Upper Class · Pre-Unit</Typography>
              </Box>
              <CardContent sx={{ p: 3.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ bgcolor: '#f0fdf4', p: 2.5, borderRadius: 2, mb: 2, border: '1px solid #bbf7d0' }}>
                  <Typography variant="subtitle2" sx={{ color: schoolColors.green, fontWeight: 800, mb: 1 }}>
                    ADA YA MWAKA (AWAMU 4):
                  </Typography>
                  <Typography variant="h4" sx={{ color: schoolColors.green, fontWeight: 900, mb: 1 }}>
                    TSh 1,000,000/=
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#166534' }}>
                    INCLUDES: Breakfast (Kifungua kinywa), Lunch (Chakula cha mchana) &amp; Usafiri wa Shule (School Bus)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  <Chip icon={<DirectionsBus />} label="Free School Bus" size="small" />
                  <Chip icon={<CheckCircle />} label="Nutritious Meals" size="small" />
                  <Chip icon={<CheckCircle />} label="British &amp; NECTA Curriculum" size="small" />
                  <Chip icon={<CheckCircle />} label="Computer Studies" size="small" />
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => navigate('/primary/apply')}
                  sx={{ bgcolor: schoolColors.green, fontWeight: 700, borderRadius: 2, py: 1.3, mt: 'auto', '&:hover': { bgcolor: '#1b5e20' } }}
                  endIcon={<ArrowForward />}
                >
                  Apply For Pre-School
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Primary Std 1 - 6 Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 4, height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', border: `2px solid ${schoolColors.teal}40` }}>
              <Box sx={{ p: 3, bgcolor: schoolColors.teal, color: 'white' }}>
                <Chip label="Ages 6 - 13 Years" size="small" sx={{ bgcolor: schoolColors.gold, color: schoolColors.dark, fontWeight: 800, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 900 }}>DARASA LA 1 HADI LA 6</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>English Medium Day &amp; Boarding Options</Typography>
              </Box>
              <CardContent sx={{ p: 3.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ bgcolor: '#f0fdfa', p: 2, borderRadius: 2, border: '1px solid #ccfbf1', height: '100%' }}>
                      <Typography variant="caption" sx={{ color: schoolColors.teal, fontWeight: 800, display: 'block' }}>
                        PRIMARY - DAY (KUTWA)
                      </Typography>
                      <Typography variant="h5" sx={{ color: schoolColors.teal, fontWeight: 900, my: 0.5 }}>
                        TSh 1,000,000/=
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#0f766e', display: 'block' }}>
                        Inajumuisha Breakfast, Lunch &amp; Usafiri wa Bus
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ bgcolor: '#eff6ff', p: 2, borderRadius: 2, border: '1px solid #bfdbfe', height: '100%' }}>
                      <Typography variant="caption" sx={{ color: schoolColors.primary, fontWeight: 800, display: 'block' }}>
                        PRIMARY - BOARDING (BWENI)
                      </Typography>
                      <Typography variant="h5" sx={{ color: schoolColors.primary, fontWeight: 900, my: 0.5 }}>
                        TSh 1,400,000/=
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#1e40af', display: 'block' }}>
                        Inajumuisha Full Meals &amp; Hostels Accommodation
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  <Chip icon={<Computer />} label="Computer Lab" size="small" />
                  <Chip icon={<CheckCircle />} label="English Only Policy" size="small" />
                  <Chip icon={<CheckCircle />} label="First Aid Clinic" size="small" />
                  <Chip icon={<CheckCircle />} label="Sports &amp; Talents" size="small" />
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => navigate('/primary/apply')}
                  sx={{ bgcolor: schoolColors.teal, fontWeight: 700, borderRadius: 2, py: 1.3, mt: 'auto', '&:hover': { bgcolor: '#004d40' } }}
                  endIcon={<ArrowForward />}
                >
                  Apply For Primary Std 1-6
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* ── Key School Features ── */}
      <Box sx={{ bgcolor: schoolColors.dark, color: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: 'white', mb: 1 }}>
              Why Choose Kaozya Schools?
            </Typography>
            <Typography variant="subtitle1" sx={{ color: schoolColors.gold, maxWidth: 600, mx: 'auto' }}>
              Quality Education For Better Future
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {[
              { icon: <Security sx={{ fontSize: 40 }} />, title: 'Usalama & Ulinzi', desc: 'Ulinzi wa saa 24, kamera za usalama na mazingira yenye amani na utulivu kwa wanafunzi wote.' },
              { icon: <People sx={{ fontSize: 40 }} />, title: 'Walimu Waliobobea', desc: 'Walimu wenye weledi wa hali ya juu, wenye maadili mema na uzoefu wa kufundisha kwa ufanisi.' },
              { icon: <DirectionsBus sx={{ fontSize: 40 }} />, title: 'Usafiri wa Uhakika', desc: 'Mabasi ya kisasa ya shule (School Bus) yanayowafikia wanafunzi maeneo yote ya Mlimba na vitongoji vyake.' },
              { icon: <Computer sx={{ fontSize: 40 }} />, title: 'ICT & Maabara ya Kisasa', desc: 'Mafunzo ya kompyuta kwa vitendo na maabara zilizokamilika za Sayansi (Physics, Chemistry, Biology).' },
            ].map((f, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Paper
                  sx={{
                    p: 3,
                    height: '100%',
                    bgcolor: 'rgba(255,255,255,0.06)',
                    color: 'white',
                    borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    '&:hover': { bgcolor: `${schoolColors.accent}30`, borderColor: schoolColors.gold },
                  }}
                >
                  <Box sx={{ color: schoolColors.gold, mb: 1.5 }}>{f.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: schoolColors.gold }}>
                    {f.title}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.85, lineHeight: 1.6 }}>
                    {f.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Lightbox Image Modal ── */}
      <Dialog
        open={Boolean(selectedPreviewImage)}
        onClose={() => setSelectedPreviewImage(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 1, bgcolor: '#000', position: 'relative', textAlign: 'center' }}>
          <IconButton
            onClick={() => setSelectedPreviewImage(null)}
            sx={{ position: 'absolute', top: 8, right: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: schoolColors.accent } }}
          >
            <CloseIcon />
          </IconButton>
          {selectedPreviewImage && (
            <Box
              component="img"
              src={selectedPreviewImage}
              alt="Preview"
              sx={{ width: '100%', maxHeight: '85vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default HomePage;
